import { type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';

import { X } from 'lucide-react';

import { type CmsDecision, CmsInteractionContext as Context } from './use-cms-interactions';

/** Native modal: inert background, focus containment, Escape and restored trigger. */
export function SiteCmsDialog({
  title,
  children,
  onClose,
  busy = false,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  busy?: boolean;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  const close = useRef(onClose);
  useEffect(() => {
    close.current = onClose;
  }, [onClose]);
  useEffect(() => {
    const trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = ref.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => {
      dialog?.close();
      if (trigger?.isConnected) trigger.focus();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      aria-labelledby={id}
      className={`cms-dialog ${wide ? 'cms-dialog-wide' : ''}`}
      onCancel={event => {
        event.preventDefault();
        if (!busy) close.current();
      }}
    >
      <header className='cms-dialog-heading'>
        <h2 id={id}>{title}</h2>
        <button
          type='button'
          className='cms-icon-button'
          aria-label='Fermer la fenêtre'
          disabled={busy}
          onClick={onClose}
        >
          <X size={20} />
        </button>
      </header>
      <div className='cms-dialog-body'>{children}</div>
    </dialog>
  );
}
type Request = CmsDecision & { resolve: (value: string | null) => void };
const formLabels: Record<string, string> = {
  'new-page': 'Nouvelle page',
  'page-address': 'Adresse de page',
  'image-upload': 'Import d’image',
  'image-metadata': 'Description d’image',
  'document-upload': 'Import PDF',
  'document-rename': 'Titre du PDF',
  'pdf-upload': 'Import PDF',
  'pdf-title': 'Titre du PDF',
};
export function SiteCmsInteractions({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<Request | null>(null);
  const [pending, setPendingMap] = useState<Record<string, boolean>>({});
  const [busy, setBusyMap] = useState<Record<string, boolean>>({});
  const current = useRef<Request | null>(null);
  const ask = useCallback(
    (decision: CmsDecision) =>
      new Promise<string | null>(resolve => {
        // Never stack confirmation dialogs or leave a previous request pending.
        current.current?.resolve(null);
        const next = { ...decision, resolve };
        current.current = next;
        setRequest(next);
      }),
    [],
  );
  const setPending = useCallback(
    (key: string, value: boolean) =>
      setPendingMap(before => (before[key] === value ? before : { ...before, [key]: value })),
    [],
  );
  const setBusy = useCallback(
    (key: string, value: boolean) =>
      setBusyMap(before => (before[key] === value ? before : { ...before, [key]: value })),
    [],
  );
  useEffect(() => () => current.current?.resolve(null), []);
  function finish(value: string | null) {
    current.current?.resolve(value);
    current.current = null;
    setRequest(null);
  }
  return (
    <Context.Provider
      value={{
        ask,
        setPending,
        pendingCount: Object.values(pending).filter(Boolean).length,
        pendingLabels: Object.entries(pending)
          .filter(([, value]) => value)
          .map(([key]) => formLabels[key] ?? key.split(':')[0]),
        setBusy,
        busyCount: Object.values(busy).filter(Boolean).length,
      }}
    >
      {children}
      {request && (
        <SiteCmsDialog title={request.title} onClose={() => finish(null)}>
          <p className='cms-dialog-message'>{request.message}</p>
          <div className='cms-dialog-actions'>
            <button autoFocus type='button' className='cms-button' onClick={() => finish(null)}>
              Annuler
            </button>
            {request.choices.map(choice => (
              <button
                key={choice.value}
                type='button'
                className={`cms-button ${choice.danger ? 'cms-danger' : 'cms-button-primary'}`}
                onClick={() => finish(choice.value)}
              >
                {choice.label}
              </button>
            ))}
          </div>
        </SiteCmsDialog>
      )}
    </Context.Provider>
  );
}
